import React, { useState } from 'react';
import {
    Alert,
    AlertDescription,
    AlertIcon,
    Box,
    Button,
    Container,
    FormControl,
    FormLabel,
    Heading,
    HStack,
    Input,
    Link as ChakraLink,
    Text,
    VStack,
    useToast,
} from '@chakra-ui/react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Navbar from '../compnents/Navbar';
import Footer from '../compnents/Footer';

const isProduction = process.env.NODE_ENV === 'production';
const API_BASE_URL = isProduction ? process.env.REACT_APP_API_BASE_URL_PROD : process.env.REACT_APP_API_BASE_URL_DEV;

const darkInputProps = {
    bg: 'rgba(255, 255, 255, 0.05)',
    color: 'white',
    borderColor: 'brand.quantumBorder',
    _placeholder: { color: 'whiteAlpha.500' },
    _hover: { borderColor: 'brand.quantumAccent' },
    _focus: {
        borderColor: 'brand.quantumAccent',
        boxShadow: '0 0 0 1px var(--chakra-colors-brand-quantumAccent)',
    },
};

const ResetPassword = () => {
    const [step, setStep] = useState(1);
    const [email, setEmail] = useState('');
    const [otp, setOtp] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [successMessage, setSuccessMessage] = useState('');
    const navigate = useNavigate();
    const toast = useToast();

    const handleRequestOTP = async (e) => {
        e.preventDefault();
        setError('');
        setSuccessMessage('');

        if (!email.trim()) {
            setError('Email is required');
            return;
        }

        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
            setError('Please enter a valid email address');
            return;
        }

        setLoading(true);

        try {
            const response = await axios.post(`${API_BASE_URL}/auth/request-password-reset`, {
                email,
            }, {
                headers: {
                    'Content-Type': 'application/json',
                },
            });

            setSuccessMessage(response.data.message || 'OTP sent to your email');
            toast({
                title: 'Reset code sent',
                description: 'Password reset OTP sent to your email',
                status: 'success',
                duration: 4000,
                isClosable: true,
            });

            setStep(2);
        } catch (requestError) {
            let errorMessage = 'Something went wrong. Please try again.';

            if (requestError.response) {
                errorMessage = requestError.response.data.message || errorMessage;
            } else if (requestError.request) {
                errorMessage = 'Unable to connect to server. Please check your connection.';
            }

            setError(errorMessage);
            toast({
                title: 'Error',
                description: errorMessage,
                status: 'error',
                duration: 4000,
                isClosable: true,
            });
        } finally {
            setLoading(false);
        }
    };

    const handleResetPassword = async (e) => {
        e.preventDefault();
        setError('');
        setSuccessMessage('');

        if (!otp.trim()) {
            setError('OTP is required');
            return;
        }

        if (!newPassword) {
            setError('New password is required');
            return;
        }

        if (newPassword.length < 6) {
            setError('Password must be at least 6 characters long');
            return;
        }

        if (newPassword !== confirmPassword) {
            setError('Passwords do not match');
            return;
        }

        setLoading(true);

        try {
            const response = await axios.post(`${API_BASE_URL}/auth/reset-password`, {
                email,
                otp,
                newPassword,
            }, {
                headers: {
                    'Content-Type': 'application/json',
                },
            });

            toast({
                title: 'Password reset successful',
                description: response.data.message || 'Your password has been reset successfully',
                status: 'success',
                duration: 4000,
                isClosable: true,
            });

            setTimeout(() => {
                navigate('/login');
            }, 1500);
        } catch (resetError) {
            let errorMessage = 'Something went wrong. Please try again.';

            if (resetError.response) {
                errorMessage = resetError.response.data.message || errorMessage;
            } else if (resetError.request) {
                errorMessage = 'Unable to connect to server. Please check your connection.';
            }

            setError(errorMessage);
            toast({
                title: 'Error',
                description: errorMessage,
                status: 'error',
                duration: 4000,
                isClosable: true,
            });
        } finally {
            setLoading(false);
        }
    };

    const handleResendOTP = async () => {
        setError('');
        setLoading(true);

        try {
            await axios.post(`${API_BASE_URL}/auth/request-password-reset`, {
                email,
            }, {
                headers: {
                    'Content-Type': 'application/json',
                },
            });

            toast({
                title: 'OTP resent',
                description: 'A new OTP has been sent to your email',
                status: 'success',
                duration: 3000,
                isClosable: true,
            });
        } catch (resendError) {
            toast({
                title: 'Error',
                description: 'Failed to resend OTP. Please try again.',
                status: 'error',
                duration: 3000,
                isClosable: true,
            });
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box minH="100vh" display="flex" flexDirection="column" bg="brand.quantumBg">
            <Navbar />

            <Box className="grid-bg-dark quantum-mesh" flex="1" overflow="hidden">
                <Container maxW="md" position="relative" zIndex={1} py={{ base: 14, md: 20 }}>
                    <Box
                        bg="brand.quantumCard"
                        p={{ base: 6, md: 8 }}
                        borderRadius="2xl"
                        boxShadow="0 24px 80px rgba(0, 0, 0, 0.38)"
                        borderWidth="1px"
                        borderColor="brand.quantumBorder"
                    >
                        <Text
                            textAlign="center"
                            color="brand.quantumAccent"
                            fontSize="xs"
                            fontWeight="800"
                            letterSpacing="0.15em"
                            textTransform="uppercase"
                            mb={3}
                        >
                            Password recovery
                        </Text>
                        <Heading as="h1" size="xl" mb={2} textAlign="center" color="white" fontWeight="bold">
                            Reset your workspace password
                        </Heading>
                        <Text textAlign="center" color="brand.quantumMuted" mb={6} fontSize="md">
                            {step === 1
                                ? 'Enter your email to receive a password reset code.'
                                : 'Enter the OTP sent to your email and create a new password.'}
                        </Text>

                        {error ? (
                            <Alert status="error" mb={4} borderRadius="md">
                                <AlertIcon />
                                <AlertDescription>{error}</AlertDescription>
                            </Alert>
                        ) : null}

                        {successMessage && step === 1 ? (
                            <Alert status="success" mb={4} borderRadius="md">
                                <AlertIcon />
                                <AlertDescription>{successMessage}</AlertDescription>
                            </Alert>
                        ) : null}

                        {step === 1 ? (
                            <form onSubmit={handleRequestOTP}>
                                <VStack spacing={4}>
                                    <FormControl isRequired>
                                        <FormLabel fontWeight="700" color="white">Email</FormLabel>
                                        <Input
                                            type="email"
                                            value={email}
                                            onChange={(e) => setEmail(e.target.value)}
                                            placeholder="you@company.com"
                                            size="lg"
                                            {...darkInputProps}
                                        />
                                    </FormControl>

                                    <Button
                                        type="submit"
                                        width="full"
                                        mt={4}
                                        size="lg"
                                        isLoading={loading}
                                        loadingText="Sending OTP..."
                                        bgGradient="linear(135deg, brand.quantumAccent, brand.quantumCyan)"
                                        color="brand.quantumBg"
                                        _hover={{ opacity: 0.9 }}
                                        fontWeight="800"
                                    >
                                        Send reset code
                                    </Button>

                                    <Text textAlign="center" color="brand.quantumMuted" fontSize="sm">
                                        Reset email usually arrives within 30 seconds. Check spam if not received in 2 minutes.
                                    </Text>

                                    <Text textAlign="center" color="brand.quantumMuted" fontSize="sm">
                                        If you are a Relationship Manager, Compliance Officer, or Portfolio Adviser and cannot sign in,
                                        your firm admin can reset your password from the Employees console. If you do not know your admin,
                                        email{' '}
                                        <ChakraLink href="mailto:compliance@finansee.eu" color="brand.quantumAccent">
                                            compliance@finansee.eu
                                        </ChakraLink>
                                        .
                                    </Text>

                                    <HStack spacing={2} mt={2}>
                                        <Text color="brand.quantumMuted">Remember your password?</Text>
                                        <ChakraLink as={Link} to="/login" color="brand.quantumAccent" fontWeight="800">
                                            Login
                                        </ChakraLink>
                                    </HStack>
                                </VStack>
                            </form>
                        ) : (
                            <form onSubmit={handleResetPassword}>
                                <VStack spacing={4}>
                                    <FormControl isRequired>
                                        <FormLabel fontWeight="700" color="white">Email</FormLabel>
                                        <Input
                                            type="email"
                                            value={email}
                                            isDisabled
                                            size="lg"
                                            bg="whiteAlpha.100"
                                            color="whiteAlpha.800"
                                            borderColor="brand.quantumBorder"
                                        />
                                    </FormControl>

                                    <FormControl isRequired>
                                        <FormLabel fontWeight="700" color="white">OTP Code</FormLabel>
                                        <Input
                                            type="text"
                                            value={otp}
                                            onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                                            placeholder="Enter 6-digit code"
                                            size="lg"
                                            maxLength={6}
                                            {...darkInputProps}
                                        />
                                    </FormControl>

                                    <FormControl isRequired>
                                        <FormLabel fontWeight="700" color="white">New Password</FormLabel>
                                        <Input
                                            type="password"
                                            value={newPassword}
                                            onChange={(e) => setNewPassword(e.target.value)}
                                            placeholder="Enter new password"
                                            size="lg"
                                            {...darkInputProps}
                                        />
                                        <Text fontSize="sm" color="brand.quantumMuted" mt={1}>
                                            Password must be at least 6 characters long.
                                        </Text>
                                    </FormControl>

                                    <FormControl isRequired>
                                        <FormLabel fontWeight="700" color="white">Confirm New Password</FormLabel>
                                        <Input
                                            type="password"
                                            value={confirmPassword}
                                            onChange={(e) => setConfirmPassword(e.target.value)}
                                            placeholder="Confirm new password"
                                            size="lg"
                                            {...darkInputProps}
                                        />
                                    </FormControl>

                                    <Button
                                        type="submit"
                                        width="full"
                                        mt={4}
                                        size="lg"
                                        isLoading={loading}
                                        loadingText="Resetting password..."
                                        bgGradient="linear(135deg, brand.quantumAccent, brand.quantumCyan)"
                                        color="brand.quantumBg"
                                        _hover={{ opacity: 0.9 }}
                                        fontWeight="800"
                                    >
                                        Reset password
                                    </Button>

                                    <HStack spacing={2} mt={2} width="full" justify="space-between">
                                        <Button
                                            variant="outline"
                                            borderColor="whiteAlpha.300"
                                            color="white"
                                            _hover={{ bg: 'whiteAlpha.100' }}
                                            onClick={handleResendOTP}
                                            isDisabled={loading}
                                            size="sm"
                                        >
                                            Resend OTP
                                        </Button>
                                        <Button
                                            variant="ghost"
                                            color="brand.quantumAccent"
                                            _hover={{ bg: 'whiteAlpha.100', color: 'brand.quantumCyan' }}
                                            onClick={() => setStep(1)}
                                            isDisabled={loading}
                                            size="sm"
                                        >
                                            Change email
                                        </Button>
                                    </HStack>

                                    <Button
                                        as={Link}
                                        to="/login"
                                        variant="outline"
                                        borderColor="whiteAlpha.300"
                                        color="white"
                                        _hover={{ bg: 'whiteAlpha.100' }}
                                        width="full"
                                        mt={2}
                                        size="md"
                                    >
                                        Back to login
                                    </Button>
                                </VStack>
                            </form>
                        )}
                    </Box>
                </Container>
            </Box>

            <Footer />
        </Box>
    );
};

export default ResetPassword;
